import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";
import SortableTable from "../../../components/SortableTable";
import { getApi } from "../../../utils/api";
import {
  API_GET_SYSTEM_LOGS,
  API_GET_SYSTEM_LOGS_BY_EMPID,
  API_GET_SYSTEM_LOGS_REPORT,
} from "../../../config/Endpoints";
import { FiEye } from "react-icons/fi";
import {
  displayFormatDate,
  formatScreenTime,
  formatDateTime,
} from "../../../utils/helpers";
import Pagination from "../../../components/Pagination";
import { toast } from "react-toastify";
import { formatToDateOnly } from "../../../utils/helpers";

const AssignedSystemLogs = ({
  employees,
  loadingEmployees,
  selectedEmployee,
  setSelectedEmployee,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [detailLogs, setDetailLogs] = useState([]);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isEmployeeFiltered, setIsEmployeeFiltered] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const accessToken = Cookies.get("accessToken");

  useEffect(() => {
    fetchLogs();
  }, [currentPage, limit]);

  useEffect(() => {
    if (!selectedEmployee && !selectedDate) {
      fetchLogs();
    }
  }, [selectedEmployee, selectedDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEmployee]);

  const fetchLogs = () => {
    setIsLoadingLogs(true);

    let url = API_GET_SYSTEM_LOGS + `?page=${currentPage - 1}&size=${limit}`;

    if (selectedEmployee?.value) {
      url += `&employeeId=${selectedEmployee.value}`;
    }

    if (selectedDate) {
      url += `&reportDate=${selectedDate.toLocaleDateString("en-CA")}`;
    }

    getApi(url, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setLogs(res.data.content);
          setTotalPages(res.data.totalPages);
          setIsEmployeeFiltered(!!selectedEmployee?.value);
        }
        setIsLoadingLogs(false);
      })
      .catch((err) => {
        console.log(err);
        setLogs([]);
        setTotalPages(1);
        toast.error(err?.response?.data?.message || "Failed to fetch logs.");
        setIsLoadingLogs(false);
      });
  };

  const fetchDetailLogs = async (batchId, employeeId) => {
    setIsDetailLoading(true);
    setDetailLogs([]);

    try {
      const url = `${API_GET_SYSTEM_LOGS_BY_EMPID}?batchId=${batchId}&employeeId=${employeeId}`;

      const res = await getApi(url, accessToken);

      if (res.status === 200) {
        setDetailLogs(res.data || []);
      }
    } catch (error) {
      setDetailLogs([]);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const getColumns = () => {
    const commonColumns = [
      // { key: "deviceName", label: "Device Name" },
      // { key: "serialNumber", label: "Serial Number" },
      {
        key: "reportDate",
        label: "Report Date",
        render: (row) =>
          row.reportDate ? displayFormatDate(row.reportDate) : "-",
        compare: (a, b) => new Date(a.reportDate) - new Date(b.reportDate),
      },
      {
        key: "loginMinutes",
        label: "Login Duration",
        render: (row) =>
          row.loginMinutes ? formatScreenTime(row.loginMinutes) : "-",
      },
      {
        key: "lockedMinutes",
        label: "Locked Duration",
        render: (row) =>
          row.lockedMinutes ? formatScreenTime(row.lockedMinutes) : "-",
      },
      {
        key: "location",
        label: "Location",
        render: (row) =>
          row.location ? row.location : "-",
      },
    ];

    const actionColumn = {
      key: "action",
      label: "Action",
      sortable: false,
      render: (row) => (
        <button
          type="button"
          className="action-btn action-btn--view"
          data-bs-toggle="modal"
          data-bs-target="#viewLogsModal"
          onClick={() => fetchDetailLogs(row.batchId, row.employeeId)}
        >
          <FiEye size={14} />
          View
        </button>
      ),
    };

    if (isEmployeeFiltered) {
      return [...commonColumns, actionColumn];
    }

    return [
      { key: "userName", label: "Employee Name" },
      ...commonColumns,
      actionColumn,
    ];
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const params = new URLSearchParams();

      if (selectedEmployee?.value) {
        params.append("employeeId", selectedEmployee.value);
      }

      if (selectedDate) {
        params.append("reportDate", formatToDateOnly(selectedDate));
      }

      // Only add query string if there are any params
      const url =
        params.toString().length > 0
          ? `${API_GET_SYSTEM_LOGS_REPORT}?${params.toString()}`
          : API_GET_SYSTEM_LOGS_REPORT;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || "Failed to download report");
        return;
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "System_Logs_Report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setLoadingExport(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <>
      <div className="filters d-flex justify-content-center gap-2 align-items-end mb-3">
        <div className="col-12 col-md-3 ">
          <label className="label">Employee Name</label>
          <Select
            className="pr"
            options={employees}
            placeholder="Select Employee Name"
            value={selectedEmployee}
            onChange={(opt) => setSelectedEmployee(opt)}
            isClearable
          />
        </div>

        <div className="col-12 col-md-3 ">
          <label className="label">Report Date</label>
          <DatePicker
            showIcon
            toggleCalendarOnIconClick
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="MM/dd/yyyy"
            placeholderText="mm/dd/yyyy"
            className="pr datepicker"
            isClearable
          />
        </div>

        <div className="col-12 col-md-2 d-flex gap-2 justify-content-start mt-2 mt-md-0">
          <button
            className="theme-button"
            onClick={() => {
              setCurrentPage(1);
              fetchLogs();
            }}
          >
            Filter
          </button>
          <button
            className="theme-button"
            onClick={handleExport}
            disabled={loadingExport}
          >
            {loadingExport ? (
              <ThreeDots height="20" width="40" radius="3" color="white" />
            ) : (
              "Export"
            )}
          </button>
        </div>
      </div>

      {isLoadingLogs && !loadingEmployees && (
        <div className="d-flex justify-content-center">
          <ThreeDots height="50" width="50" visible color="blue" />
        </div>
      )}

      {!isLoadingLogs && logs.length === 0 && (
        <div className="fs-5 text-secondary text-center">
          No records available
        </div>
      )}

      {!isLoadingLogs && logs.length > 0 && (
        <>
          {" "}
          <SortableTable
            columns={getColumns()}
            data={logs}
            currentPage={currentPage}
            limit={limit}
            className="resume custom"
          />{" "}
          <div className="row">
            <div className="col-md-3">
              <div>
                <label htmlFor="itemsPerPage">Items per page </label>
                <select
                  id="itemsPerPage"
                  onChange={handleLimitChange}
                  value={limit}
                  className="border ms-3 w-25  p-2"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                </select>
              </div>
            </div>
            <div className="col-md-9">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </>
      )}

      <div className="modal fade" id="viewLogsModal" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-center flex">
                System Log Details
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              {isDetailLoading && (
                <div className=" d-flex justify-content-center">
                  <ThreeDots height="40" width="40" visible color="blue" />
                </div>
              )}

              {!isDetailLoading && detailLogs.length > 0 && (
                <>
                  <SortableTable
                    columns={[
                      // { key: "deviceName", label: "Device Name" },
                      {
                        key: "serialNumber",
                        label: "Serial Number",
                        render: (row) => row.serialNumber || "-",
                      },

                      {
                        key: "reportDate",
                        label: "Report Date",
                        render: (row) =>
                          row.reportDate
                            ? displayFormatDate(row.reportDate)
                            : "-",
                        compare: (a, b) =>
                          new Date(a.reportDate) - new Date(b.reportDate),
                      },
                      {
                        key: "startTime",
                        label: "Start Time",
                        render: (row) =>
                          row.startTime ? formatDateTime(row.startTime) : "-",
                        compare: (a, b) =>
                          new Date(a.startTime) - new Date(b.startTime),
                      },
                      { key: "eventType", label: "Event Type" },
                      {
                        key: "durationMinutes",
                        label: "Duration ",
                        render: (row) =>
                          row.durationMinutes
                            ? formatScreenTime(row.durationMinutes)
                            : "-",
                      },
                    ]}
                    data={detailLogs}
                  />
                </>
              )}

              {!isDetailLoading && detailLogs.length === 0 && (
                <div className="text-center text-muted">No details found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignedSystemLogs;
